import {
  Avatar,
  Divider,
  Fab,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Slide,
  Badge,
  Drawer,
  useTheme,
  useMediaQuery,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import SendIcon from '@mui/icons-material/Send';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HandshakeIcon from '@mui/icons-material/Handshake';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import './Chat.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fDateTime } from '../../utils/formatTime';
import { updateAnnonce } from 'src/service/annonceApi';
import { createMessage, createMessagePicture, updateMessageReadStatus, deleteMessage } from 'src/service/messageApi';
import { getUnreadMessages } from 'src/utils/messageUtils';
import { getConversation } from 'src/service/conversationApi';
import { getCheckout, createCheckout, getCheckoutPaiement, getUserCheckout, deleteCheckout } from 'src/service/checkoutApi';
import { notifySuccess } from 'src/service/notificationService';
import { useNotification } from 'src/contexts/NotificationContext';
import { ShButton } from 'src/service/componentService';
import { ToastContainer } from 'react-toastify';
import CustomSpinner from 'src/pages/CustomSpinner';
import ImageIcon from '@mui/icons-material/Image';
import ImageUploading from 'react-images-uploading';
import { Image } from 'react-bootstrap';
import { styled } from '@mui/material/styles';
import Iconify from '../../components/Iconify';

const ChatContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  width: '100%',
  height: isMobile ? '100vh' : 'calc(100vh - 200px)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: isMobile ? 0 : '8px',
  overflow: 'hidden',
  border: '1px solid #DFE3E8',
  backgroundColor: '#ffffff',
  position: 'relative',
  zIndex: 1,
}));

const UserList = styled(Box)(({ theme }) => ({
  backgroundColor: '#F9FAFB',
  borderRight: '1px solid #DFE3E8',
  height: '100%',
  width: '100%',
  overflowY: 'auto',
  padding: theme.spacing(2),
}));

const UserItem = styled(ListItem)(({ theme, selected }) => ({
  backgroundColor: selected ? '#1b9098' : 'transparent',
  borderRadius: '8px',
  margin: theme.spacing(0.5),
  border: selected ? `1px solid #1b9098` : '1px solid transparent',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: selected ? '#1b9098' : '#F4F6F8',
    transform: 'translateX(4px)',
  },
  '& .MuiListItemIcon-root': {
    minWidth: '40px',
  },
  '& .MuiListItemText-primary': {
    fontWeight: selected ? 700 : 500,
    color: selected ? '#ffffff' : '#333333',
  },
  '& .MuiListItemText-secondary': {
    color: selected ? 'rgba(255,255,255,0.8)' : '#637381',
  },
}));

const MessageList = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  backgroundColor: '#F9FAFB',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingBottom: isMobile ? theme.spacing(1) : theme.spacing(2),
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => !['isAuthor', 'isRead', 'isLastAuthorMessage', 'conversationColor', 'isNew'].includes(prop),
})(({ theme, isAuthor, isRead, isLastAuthorMessage, conversationColor, isNew }) => {
  // Fonction pour assombrir une couleur
  const darkenColor = (color, amount = 0.3) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const darkR = Math.max(0, Math.floor(r * (1 - amount)));
    const darkG = Math.max(0, Math.floor(g * (1 - amount)));
    const darkB = Math.max(0, Math.floor(b * (1 - amount)));
    
    return '#' + 
      darkR.toString(16).padStart(2, '0') + 
      darkG.toString(16).padStart(2, '0') + 
      darkB.toString(16).padStart(2, '0');
  };

  // Déterminer la couleur de fond
  let backgroundColor;
  if (isAuthor) {
    // Messages de droite (utilisateur) : couleur de conversation assombrie
    backgroundColor = conversationColor ? darkenColor(conversationColor, 0.4) : '#007AFF';
  } else {
    // Messages de gauche (système) : couleur de conversation normale
    backgroundColor = conversationColor || '#E9E9EB';
  }

  return {
    maxWidth: '70%',
    minWidth: '100px',
    padding: theme.spacing(1.5),
    width: 'fit-content',
    borderRadius: isAuthor ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    backgroundColor: backgroundColor,
    color: isAuthor ? '#ffffff' : '#000000',
    marginBottom: theme.spacing(1),
    border: isAuthor ? 'none' : 'none',
    alignSelf: isAuthor ? 'flex-end' : 'flex-start',
    opacity: isRead && !isLastAuthorMessage ? 0.6 : 1,
    filter: isRead && !isLastAuthorMessage ? 'grayscale(0.3)' : 'none',
    transition: 'opacity 0.3s ease, filter 0.3s ease',
    boxShadow: isAuthor ? '0 1px 2px rgba(0, 0, 0, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    // Animation pour les nouveaux messages
    animation: isNew ? `${isAuthor ? 'slideInRight' : 'slideInLeft'} 0.4s ease-out` : 'none',
    transform: isNew ? 'translateX(0)' : 'none',
    '@keyframes slideInRight': {
      '0%': {
        opacity: 0,
        transform: 'translateX(30px) scale(0.8)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateX(0) scale(1)',
      },
    },
    '@keyframes slideInLeft': {
      '0%': {
        opacity: 0,
        transform: 'translateX(-30px) scale(0.8)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateX(0) scale(1)',
      },
    },
  };
});

const MessageTime = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isAuthor',
})(({ theme, isAuthor }) => ({
  fontSize: '0.75rem',
  color: '#637381',
  marginTop: theme.spacing(0.5),
  opacity: 0.7,
  textAlign: isAuthor ? 'right' : 'left',
}));

const InputContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: '#ffffff',
  borderTop: '1px solid #DFE3E8',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  position: isMobile ? 'fixed' : 'sticky',
  bottom: isMobile ? 0 : 'auto',
  left: 0,
  right: 0,
  zIndex: isMobile ? 1000 : 1,
  borderBottom: isMobile ? '1px solid #DFE3E8' : 'none',
}));

const ActionButton = styled(Fab)(({ theme }) => ({
  backgroundColor: '#1b9098',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#135F64',
  },
  minWidth: '40px',
  height: '40px',
}));



export default function Chat({ annonce, onClose }) {
  const navigate = useNavigate();
  const { triggerRefresh } = useNotification();
  const [searchParams] = useSearchParams();
  const componentRef = useRef();
  const listRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [action, setAction] = useState('');
  const [objectStatus, setObjectStatus] = useState(annonce.status)
  const [handleOnAction, setHandleOnAction] = useState('');
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState({});
  const [currentMessages, setCurrentMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSend, setIsSend] = useState(false);
  const [conversationKey, setConversationKey] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [actionSentence, setActionSentence] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [images, setImages] = useState([]);
  const [imageUpload, setImageUpload] = useState(null);
  const [loadingSendMessage, setLoadingSendMessage] = useState(false)
  const [prevMessagesLength, setPrevMessagesLength] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogRestitution, setDialogRestitution] = useState(false)
  const [dialoguePicture, setDialoguePicture] = useState(null);
  const [newMessageIds, setNewMessageIds] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [checkoutDetails, setCheckoutDetails] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutsByConversation, setCheckoutsByConversation] = useState({});
  const [currentCheckoutConversationId, setCurrentCheckoutConversationId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const isMountedRef = useRef(true);



  useEffect(() => {
    fetchConversations();
    
    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, []); // Seulement au montage du composant

  useEffect(() => {
    const id = setInterval(() => {
      if (isMountedRef.current) {
        fetchConversations();
      }
    }, 60000); // Rafraîchit toutes les 60 secondes
    
    return () => {
      clearInterval(id);
    };
  }, []); // Seulement au montage du composant

  useEffect(() => {
    if (message === '') {
      setImageUpload(null)
      setImages(null)
    }
  }, [message])

  useEffect(() => {
    if (listRef.current && currentMessages.length > prevMessagesLength) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
        setPrevMessagesLength(currentMessages.length); 
    }
  }, [currentMessages, prevMessagesLength]);

  useEffect(() => {
    if (currentMessages.length > 0 && isMountedRef.current) {
      const unreadMessages = getUnreadMessages(currentMessages);
      
      if (unreadMessages.length > 0) {
        const updateMessages = async () => {
          for (const message of unreadMessages) {
            try {
              if (!isMountedRef.current) break; // Arrêter si le composant est démonté
              
              const response = await updateMessageReadStatus(message.id, true);
              
              if (response && response.isRead && isMountedRef.current) {
              setCurrentMessages(prevMessages => 
                prevMessages.map(msg => 
                  msg.id === message.id ? { ...msg, isRead: true } : msg
                )
              );
                
                window.dispatchEvent(new CustomEvent('messageRead', { 
                  detail: { messageId: message.id } 
                }));
              }
              
              await new Promise(resolve => setTimeout(resolve, 100));
              
            } catch (error) {
              // Erreur silencieuse
            }
          }
        };

        updateMessages();
      }
    }
  }, [currentMessages]);

  const fetchConversations = async () => {
    try {
      const data = await getConversation(`?annonce=${annonce.id}`)
      data.sort((x, y) => (x.isPending === y.isPending ? 0 : x.isPending ? -1 : 1));
      if (data.length > 0) {
        const currentConv = data.find(conv => conv.id === currentConversation.id);
        if (currentConv) {
        }
      }
      
      // Vérifier si le composant est toujours monté avant de mettre à jour l'état
      if (!isMountedRef.current) return;
      
      // Vérifier si les données ont changé avant de mettre à jour
      const hasChanged = JSON.stringify(data) !== JSON.stringify(conversations);
      
      if (hasChanged) {
        setConversations(data);
        
        // Vérifier tous les checkouts au chargement
        checkAllCheckouts(data);

        // Vérifier si on doit sélectionner une conversation spécifique
        const conversationId = searchParams.get('conversation');
        const lastConversationId = localStorage.getItem('lastConversationId');
        let targetIndex = selectedIndex; // Garder l'index actuel par défaut
        
        if (conversationId) {
          const targetConversationIndex = data.findIndex(conv => conv.id === parseInt(conversationId));
          if (targetConversationIndex !== -1) {
            targetIndex = targetConversationIndex;
          }
        } else if (lastConversationId && data.length > 0) {
          // Essayer de restaurer la dernière conversation depuis le localStorage
          const lastConversationIndex = data.findIndex(conv => conv.id === parseInt(lastConversationId));
          if (lastConversationIndex !== -1) {
            targetIndex = lastConversationIndex;
          } else if (!currentConversation || !currentConversation.id || Object.keys(currentConversation).length === 0) {
            // Si la dernière conversation n'existe plus, prendre la première
            targetIndex = 0;
          }
        } else if (data.length > 0 && (!currentConversation || !currentConversation.id || Object.keys(currentConversation).length === 0)) {
          // Seulement si aucune conversation n'est sélectionnée, prendre la première
          targetIndex = 0;
        }

        // Mettre à jour la conversation sélectionnée seulement si nécessaire
        if (data.length > 0) {
          // Vérifier si la conversation actuelle existe toujours dans les nouvelles données
          const currentConvInData = data.find(conv => conv.id === currentConversation.id);
          
          if (currentConvInData) {
            // La conversation actuelle existe, on la garde et on met à jour ses messages
            if (currentConvInData.messages) {
        setCurrentMessages(
                currentConvInData.messages.sort((x, y) => x.id - y.id)
              );
            }
            // Mettre à jour l'index de sélection pour correspondre à la nouvelle position
            const newIndex = data.findIndex(conv => conv.id === currentConversation.id);
            if (newIndex !== -1 && newIndex !== selectedIndex) {
              setSelectedIndex(newIndex);
              setConversationKey(newIndex);
            }
          } else {
            // La conversation actuelle n'existe plus, sélectionner la cible ou la première
            const targetConversation = data[targetIndex] || data[0];
            setCurrentConversation(targetConversation);
            setConversationKey(targetIndex);
            setSelectedIndex(targetIndex);
            
            if (targetConversation.messages) {
              setCurrentMessages(
                targetConversation.messages.sort((x, y) => x.id - y.id)
        );
            }
          }
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Erreur lors du chargement des conversations:', error);
      }
    }
  };
  const handleConversation = (conversation, index) => {
    setCurrentConversation(conversation);
    setCurrentMessages(conversation.messages);
    setConversationKey(index);
    setSelectedIndex(index);
    
    // Sauvegarder l'ID de la conversation dans le localStorage
    localStorage.setItem('lastConversationId', conversation.id.toString());
    // Fermer le drawer sur mobile après sélection
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const forceRefreshConversations = () => {
    if (isMountedRef.current) {
      fetchConversations();
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleMessageNotOk = async () => {
    // Récupérer le message et convertir les \n en retours à la ligne réels
    let messageContent = process.env.REACT_APP_NOTOKMESSAGE || process.env.REACT_APP_NOTOKTEXT || 'Message de refus par défaut';
    messageContent = convertNewlines(messageContent);
    
    const data = {
      conversation: `api/conversations/${currentConversation.id}`,
      content: messageContent,
    };



    try {
      const newMsg = await createMessage(data);
      // Ajout immédiat du message dans le chat
      const messageWithData = {
        ...newMsg,
        createdAt: newMsg.createdAt || new Date().toISOString(),
        author: newMsg.author || (currentConversation && currentConversation.customer ? currentConversation.customer : null)
      };
      
      setCurrentMessages(prev => [...prev, messageWithData]);
      
      // Marquer le message comme nouveau pour l'animation
      setNewMessageIds(prev => new Set([...prev, newMsg.id || Date.now()]));
      
      // Retirer l'animation après 500ms
      setTimeout(() => {
        setNewMessageIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(newMsg.id || Date.now());
          return newSet;
        });
      }, 500);
      
      // Scroll automatique en bas après ajout du message
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      }, 100);
      setIsSend(!isSend);
      // Rafraîchissement immédiat avec petit délai
      setTimeout(() => {
        forceRefreshConversations();
      }, 100);
    } catch (error) {
      console.error(error);
    }
  };

  // Fonction pour convertir les \n en retours à la ligne réels
  const convertNewlines = (text) => {
    if (!text) return text;
    return text
      .replace(/\\n/g, '\n')  // Convertir \n en retour à la ligne
      .replace(/\\r/g, '')    // Supprimer \r
      .replace(/\\/g, '');    // Supprimer les \ restants
  };

  const handleMessageOk = async () => {
    // Remplacer les variables dynamiques dans le message
    let messageContent = (process.env.REACT_APP_OKMESSAGE || process.env.REACT_APP_OKTEXT || 'Message de validation par défaut')
      .replace('{annonce.id}', annonce.id)
      .replace('{annonce.title}', annonce.title || '')
      .replace('{annonce.category.name}', annonce.category?.name || '');
    
    // Convertir les \n en retours à la ligne réels
    messageContent = convertNewlines(messageContent);
    
    const data = {
      conversation: `api/conversations/${currentConversation.id}`,
      content: messageContent,
    };

    try {
      const newMsg = await createMessage(data);
      // Ajout immédiat du message dans le chat
      const messageWithData = {
        ...newMsg,
        createdAt: newMsg.createdAt || new Date().toISOString(),
        author: newMsg.author || (currentConversation && currentConversation.customer ? currentConversation.customer : null)
      };
      
      setCurrentMessages(prev => [...prev, messageWithData]);
      
      // Marquer le message comme nouveau pour l'animation
      setNewMessageIds(prev => new Set([...prev, newMsg.id || Date.now()]));
      
      // Retirer l'animation après 500ms
      setTimeout(() => {
        setNewMessageIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(newMsg.id || Date.now());
          return newSet;
        });
      }, 500);
      
      // Scroll automatique en bas après ajout du message
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      }, 100);
      setIsSend(!isSend);
      setTimeout(() => {
        forceRefreshConversations();
      }, 100);
    } catch (error) {
      console.error(error);
    }
  };

  const handleMessageLink = async () => {
    // Vérifier que les données nécessaires sont présentes
    if (!annonce?.id || !currentConversation?.author?.id) {
      console.error('Données manquantes pour créer le bon de livraison:', {
        annonceId: annonce?.id,
        userId: currentConversation?.author?.id
      });
      notifySuccess('Erreur: données manquantes pour créer le bon de livraison');
      return;
    }

    let data = {
      annonceId: annonce.id,
      userId: currentConversation.author.id,
    };
    
    setIsLoading(true);
    
    try {
      const checkoutResponse = await createCheckout(data);
      // On considère l'action comme réussie si le checkout est créé, peu importe checkoutSessionId
      
      // Envoyer le message de confirmation
      try {
        const dataMessage = {
          conversation: `api/conversations/${currentConversation.id}`,
          content: process.env.REACT_APP_BONDELIVRAISONMESSAGE || 'Bonjour ! Nous sommes heureux de pouvoir vous rendre votre objet ! Un bordereau de livraison vient de vous être envoyé sur votre mail ! Une fois rempli, vous recevrez votre objet entre 2 et 5 jours ouvrés ! A très vite !',
        };

        const newMsg = await createMessage(dataMessage);
        // Ajout immédiat du message dans le chat
        const messageWithData = {
          ...newMsg,
          createdAt: newMsg.createdAt || new Date().toISOString(),
          author: newMsg.author || (currentConversation && currentConversation.customer ? currentConversation.customer : null)
        };
        
        setCurrentMessages(prev => [...prev, messageWithData]);
        
        // Marquer le message comme nouveau pour l'animation
        setNewMessageIds(prev => new Set([...prev, newMsg.id || Date.now()]));
        
        // Retirer l'animation après 500ms
        setTimeout(() => {
          setNewMessageIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(newMsg.id || Date.now());
            return newSet;
          });
        }, 500);
        
        // Scroll automatique en bas après ajout du message
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
          }
        }, 100);
        setIsSend(!isSend);
        setTimeout(() => {
          forceRefreshConversations();
        }, 500);
      } catch (messageError) {
        console.error('Erreur lors de l\'envoi du message:', messageError);
        // Le checkout a été créé avec succès, on peut continuer
      }

      // Déclencher le rafraîchissement des notifications, reset UI, etc.
      setMessage('');
      setHandleOnAction('');
      setAction('');
      setActionSentence('');
      setActionMessage('');
      notifySuccess('Bon de livraison envoyé !');
      // NE PAS changer le statut ni rediriger vers aexpedie ici
      
    } catch (error) {
      console.error('Erreur lors de la création du bon de livraison:', error);
      
      // Afficher plus de détails sur l'erreur
      if (error.response) {
        console.error('Réponse d\'erreur:', error.response.data);
        console.error('Status:', error.response.status);
        
        // Gestion spécifique pour l'erreur de checkout existant
        if (error.response.data?.error === 'Checkout already exist') {
          notifySuccess('Vous avez déjà envoyé un bon de livraison pour cet objet');
        } else {
          notifySuccess(`Erreur ${error.response.status}: ${error.response.data?.message || 'Erreur lors de l\'envoi du bon de livraison'}`);
        }
      } else if (error.request) {
        console.error('Erreur de requête:', error.request);
        notifySuccess('Erreur de connexion lors de l\'envoi du bon de livraison');
      } else {
        console.error('Erreur:', error.message);
        notifySuccess('Erreur lors de l\'envoi du bon de livraison');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleImprimerLeBon = async (status) => {
    try {
      setIsLoading(true);
      
      const response = await getCheckout(annonce.id);
      
        const blob = new Blob([response], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `bon-de-livraison-${annonce.id}.pdf`;
        link.click();
      
      // Mettre à jour le statut seulement si le téléchargement a réussi
      await handleUpdateAds({
          status: 'ENVOYÉ'
      }, "Bon de livraison téléchargé !", "/dashboard/products/envoyes");
      
      // Déclencher le rafraîchissement des notifications
      triggerRefresh();
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'impression du bon de livraison:', error);
      
      if (error.response) {
        console.error('Réponse d\'erreur:', error.response.status, error.response.data);
        
        if (error.response.status === 404) {
          notifySuccess('Aucun bon de livraison trouvé pour cet objet');
        } else if (error.response.status === 400) {
          notifySuccess('Le formulaire de livraison n\'a probablement pas été renseigné et payé. Veuillez attendre que le client complète le processus.');
        } else if (error.response.status === 401) {
          notifySuccess('Session expirée, veuillez vous reconnecter');
        } else {
          notifySuccess(`Erreur ${error.response.status}: Impossible de récupérer le bon de livraison`);
        }
      } else if (error.request) {
        console.error('Erreur de requête:', error.request);
        notifySuccess('Erreur de connexion lors de la récupération du bon de livraison');
      } else {
        console.error('Erreur:', error.message);
        notifySuccess('Erreur lors de l\'impression du bon de livraison');
      }
    } finally {
      setIsLoading(false);
    }
  }
  
  const handleMessage = async () => {
    if (message !== '') {
      setLoadingSendMessage(true);
      let data = {
        conversation: `api/conversations/${currentConversation.id}`,
        content: message,
      };

      try {
      if (imageUpload) {
        const formData = new FormData();
        formData.append('picture', imageUpload);
        formData.append('conversation', currentConversation.id);
        const newMsg = await createMessagePicture(formData);
        // Ajout immédiat du message image dans le chat avec flag isPicture et champs requis
        const messageWithData = {
          ...newMsg,
          isPicture: true,
          createdAt: newMsg.createdAt || new Date().toISOString(),
          author: newMsg.author || (currentConversation && currentConversation.customer ? currentConversation.customer : null)
        };
        
        setCurrentMessages(prev => [...prev, messageWithData]);
        
        // Marquer le message comme nouveau pour l'animation
        setNewMessageIds(prev => new Set([...prev, newMsg.id || Date.now()]));
        
        // Retirer l'animation après 500ms
        setTimeout(() => {
          setNewMessageIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(newMsg.id || Date.now());
            return newSet;
          });
        }, 500);
        // Scroll automatique en bas après ajout du message image
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
          }
        }, 100);
      } else {
        const newMsg = await createMessage(data);
        // Ajout immédiat du message texte dans le chat
        const messageWithData = {
          ...newMsg,
          createdAt: newMsg.createdAt || new Date().toISOString(),
          author: newMsg.author || (currentConversation && currentConversation.customer ? currentConversation.customer : null)
        };
        
        setCurrentMessages(prev => [...prev, messageWithData]);
        
        // Marquer le message comme nouveau pour l'animation
        setNewMessageIds(prev => new Set([...prev, newMsg.id || Date.now()]));
        
        // Retirer l'animation après 500ms
        setTimeout(() => {
          setNewMessageIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(newMsg.id || Date.now());
            return newSet;
          });
        }, 500);
        // Scroll automatique en bas après ajout du message
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
          }
        }, 100);
      }

      setLoadingSendMessage(false);
      setMessage('');
      setImageUpload(null);
      setImages(null);
      
      // Rafraîchissement immédiat avec petit délai
      setTimeout(() => {
        forceRefreshConversations();
      }, 100);
      } catch (error) {
        console.error('Erreur lors de l\'envoi du message:', error);
        setLoadingSendMessage(false);
      }
    }
  };

  const getPicture = (content) => {
    const imageUrl = `data:image/png;base64, ${content}`
    return imageUrl
  }

  const handlePicture = (img) => {
    setImages(img)
    setImageUpload(img[0] ? img[0].file : null);
    setMessage(`*${img[0].file.name}*`)
  }

  const deletePicture = () => {
    setImageUpload(null)
    setImages(null)
    setMessage('')
  }

  // Fonction pour formater la date au centre (style iMessage)
  const formatDateHeader = (date) => {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (messageDate.toDateString() === today.toDateString()) {
      return 'Aujourd\'hui';
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Hier';
    } else {
      return messageDate.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  // Fonction pour formater l'heure (style iMessage)
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction pour grouper les messages par jour
  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentGroup = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.createdAt);
      const messageDay = messageDate.toDateString();

      if (!currentGroup || currentGroup.date !== messageDay) {
        // Nouveau groupe
        currentGroup = {
          date: messageDay,
          displayDate: formatDateHeader(message.createdAt),
          messages: []
        };
        groups.push(currentGroup);
      }

      currentGroup.messages.push(message);
    });

    return groups;
  };

  const handleUpdateAds = async (data, notifMessage = "", path = "") => {
    try {
      setIsLoading(true);
      await updateAnnonce(annonce.id, data).then((res) => {
        setMessage('');
        setHandleOnAction('');
        setAction('');
        setActionSentence('');
        setActionMessage('');
        setTimeout(() => {
          forceRefreshConversations();
        }, 500);
        if (notifMessage !== '')
          notifySuccess(notifMessage);
        if (path !== '')
          navigate(path);
        
        // Déclencher le rafraîchissement des notifications
        triggerRefresh();
      })
    } catch (error) {
      console.error(error);
    }
  }

  const handleClickOpen = (action) => {
    switch (action) {
      case 'lien':
        setActionSentence(`Voulez vous envoyer un lien a de restitution à ${currentConversation.author.email} : `);
        setActionMessage(
          "Cette opération est irrémédiable et permettra à la personne d'acceder au formulaire qui créera le bon de livraison"
        );
        setAction('Envoyer un lien');
        setHandleOnAction('lien');
        setOpen(true);
        break;
      case 'restitution':
        setActionSentence(`Voulez vous restituer cet Objet : ${annonce.category.name} ${annonce.id}`);
        setActionMessage('L\'objet sera disponible dans la sections "Objets restitués" ');
        setDialogRestitution(true)
        setAction('Restituer');
        setHandleOnAction('restitution');
        setOpen(true);
        break;
      case 'annulRestitution':
        setActionSentence(`Voulez vous annuler la restitution pour l' objet : ${annonce.category.name} ${annonce.id}`);
        setActionMessage(`L'objet sera disponible dans la section "objets ${annonce.origin.toLowerCase()}s"`);
        setAction('Annuler la restitution');
        setHandleOnAction('annulRestitution');
        setOpen(true);
        break;
      case 'expedition':
        setActionSentence(`Voulez vous envoyer en expédition l' objet : ${annonce.category.name} ${annonce.id}`);
        setActionMessage(`L'objet sera disponible dans la section "objets à expédier"`);
        setAction('A expedier !');
        setHandleOnAction('expedition');
        setOpen(true);
        break;

      case 'annulExpedition':
        setActionSentence(`Voulez vous annuler expédition ?`);
        setActionMessage(`L'objet sera disponible dans la section "objets Trouvés"`);
        setAction("Annuler l'expédition !");
        setHandleOnAction('annulExpedition');
        setOpen(true);
        break;

      default:
        return;
    }
  };

  const handleAction = () => {
    switch (handleOnAction) {
      case 'restitution':
        setOpen(false);
        // Fermer la modal parente immédiatement
        if (onClose) {
          onClose();
        } else {
        }
        // Mettre à jour le statut sans redirection automatique
        handleUpdateAds({
            status: "RESTITUÉ"
          }, "objet restitué", "");
        // Rediriger manuellement après un délai
        setTimeout(() => {
          navigate("/dashboard/products/restitues");
        }, 1000);
        break;
      case 'annulRestitution':
        setOpen(false);
        // Fermer la modal parente immédiatement
        if (onClose) {
          onClose();
        } else {
        }
        // Mettre à jour le statut sans redirection automatique
        handleUpdateAds({
            status: annonce.origin
          }, "restitution annulée", "");
        // Rediriger manuellement après un délai
        setTimeout(() => {
          navigate("/dashboard/products/trouves");
        }, 1000);
        break;
      case 'expedition':
        setOpen(false);
        handleUpdateAds({
            status: "A_EXPEDIER"
          }, "objet envoyé à l'expédition", "/dashboard/products/aexpedier");
        break;
      case 'annulExpedition':
        setOpen(false);
        handleUpdateAds({
            status: annonce.origin
          }, "envoi annulé", "/dashboard/products/trouves");
        break;
      case 'lien':
        setOpen(false);
        handleMessageLink();
        break;
      default:
        return;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenDialogue = (content) => {
    setDialoguePicture(getPicture(content))
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setDialoguePicture(null)
    setOpenDialog(false)
  }

  // Fonction pour générer une couleur unique basée sur l'ID de la conversation
  const getConversationColor = (conversationId) => {
    const colors = [
      '#FF6B6B', // Rouge
      '#4ECDC4', // Turquoise
      '#45B7D1', // Bleu
      '#96CEB4', // Vert
      '#FFEAA7', // Jaune
      '#DDA0DD', // Violet
      '#98D8C8', // Vert menthe
      '#F7DC6F', // Jaune doré
      '#BB8FCE', // Violet clair
      '#85C1E9', // Bleu clair
      '#F8C471', // Orange
      '#82E0AA', // Vert clair
      '#F1948A', // Rose
      '#85C1E9', // Bleu ciel
      '#D7BDE2', // Lavande
    ];
    
    const index = conversationId % colors.length;
    return colors[index];
  };

  // Fonction pour assombrir une couleur
  const darkenColor = (color, amount = 0.3) => {
    // Convertir la couleur hex en RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Assombrir en réduisant les valeurs RGB
    const darkR = Math.max(0, Math.floor(r * (1 - amount)));
    const darkG = Math.max(0, Math.floor(g * (1 - amount)));
    const darkB = Math.max(0, Math.floor(b * (1 - amount)));
    
    // Convertir en hex
    const darkHex = '#' + 
      darkR.toString(16).padStart(2, '0') + 
      darkG.toString(16).padStart(2, '0') + 
      darkB.toString(16).padStart(2, '0');
    
    return darkHex;
  };

  // Fonction pour détecter si un message est un bordereau de livraison
  const isDeliveryMessage = (message) => {
    if (!message.content) return false;
    
    // Vérifier si c'est un message de bordereau de livraison
    const isDeliveryBordereau = (
      message.content.includes('bordereau de livraison') ||
      message.content.includes('bon de livraison') ||
      message.content.includes('Bordereau de livraison') ||
      message.content.includes('Bon de livraison') ||
      (process.env.REACT_APP_BONDELIVRAISONMESSAGE && 
       message.content.includes(process.env.REACT_APP_BONDELIVRAISONMESSAGE))
    );
    
    // Vérifier si c'est un message "Je valide" (à exclure de la protection)
    const isValidationMessage = process.env.REACT_APP_OKMESSAGE && 
      message.content.includes(process.env.REACT_APP_OKMESSAGE);
    
    // Retourner true seulement pour les bordereaux, pas pour les messages de validation
    return isDeliveryBordereau && !isValidationMessage;
  };

  // Fonction pour gérer le clic droit sur un message
  const handleMessageContextMenu = (event, message) => {
    event.preventDefault();
    
    // Ne pas afficher le menu pour les messages de bordereau
    if (isDeliveryMessage(message)) {
      return;
    }
    
    setSelectedMessage(message);
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  // Fonction pour fermer le menu contextuel
  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedMessage(null);
  };

  // Fonction pour supprimer un message
  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;

    try {
      await deleteMessage(selectedMessage.id);
      
      // Supprimer le message de l'interface
      setCurrentMessages(prev => prev.filter(msg => msg.id !== selectedMessage.id));
      
      // Rafraîchir les conversations
      setTimeout(() => {
        forceRefreshConversations();
      }, 100);
      
      notifySuccess('Message supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression du message:', error);
      notifySuccess('Erreur lors de la suppression du message');
    } finally {
      handleCloseContextMenu();
    }
  };

  // Fonction pour vérifier l'existence d'un checkout
  const checkCheckoutExists = async (userId, annonceId) => {
    if (!userId || !annonceId) return false;
    
    try {
      const details = await getUserCheckout(userId, annonceId);
      return details && details.id;
    } catch (error) {
      console.error('Erreur lors de la vérification du checkout:', error);
      return false;
    }
  };

  // Fonction pour vérifier tous les checkouts au chargement
  const checkAllCheckouts = async (conversations) => {
    const checkouts = {};
    
    for (const conversation of conversations) {
      if (conversation.author?.id && annonce?.id) {
        const checkoutExists = await checkCheckoutExists(conversation.author.id, annonce.id);
        checkouts[conversation.id] = checkoutExists;
      }
    }
    
    setCheckoutsByConversation(checkouts);
  };

  // Fonction pour ouvrir les détails du bordereau
  const handleCheckoutDetails = async (conversation) => {
    if (!conversation?.author?.id || !annonce?.id) {
      notifySuccess('Impossible de récupérer les détails du bordereau');
      return;
    }

    setCheckoutLoading(true);
    try {
      const details = await getUserCheckout(conversation.author.id, annonce.id);
      setCheckoutDetails(details);
      setCurrentCheckoutConversationId(conversation.id);
      setShowCheckoutModal(true);
      

    } catch (error) {
      console.error('Erreur lors de la récupération des détails:', error);
      notifySuccess('Erreur lors de la récupération des détails du bordereau');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Fonction pour supprimer le bordereau
  const handleDeleteCheckout = async () => {
    if (!checkoutDetails?.id) return;

    try {
      await deleteCheckout(checkoutDetails.id);
      setShowCheckoutModal(false);
      setShowDeleteConfirmation(false);
      setCheckoutDetails(null);
      // Mettre à jour l'état après suppression
      if (currentCheckoutConversationId) {
        setCheckoutsByConversation(prev => ({
          ...prev,
          [currentCheckoutConversationId]: false
        }));
        setCurrentCheckoutConversationId(null);
      }
      notifySuccess('Bordereau supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression du bordereau:', error);
      notifySuccess('Erreur lors de la suppression du bordereau');
    }
  };

  // Fonction pour ouvrir la confirmation de suppression
  const handleDeleteCheckoutClick = () => {
    setShowDeleteConfirmation(true);
  };

    // Composant ConversationList défini à l'intérieur du composant principal
  const ConversationList = ({ conversations, selectedIndex, onConversationSelect, onDrawerClose, isMobile = false, onRefresh }) => {
    const getUnreadCount = (conversation) => {
      if (!conversation.messages) return 0;
      return conversation.messages.filter(message => !message.isRead && message.author).length;
    };

  return (
            <UserList>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Conversations ({conversations.length})
              </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Actualiser les conversations">
              <IconButton 
                onClick={() => {
                  onRefresh();
                }} 
                size="small"
                sx={{ color: 'primary.main' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {isMobile && (
              <IconButton onClick={onDrawerClose} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
              <Divider sx={{ mb: 2 }} />
              <List>
                {conversations.length > 0 &&
            conversations.map((conversation, index) => {
              const unreadCount = getUnreadCount(conversation);
              return (
                    <UserItem
                      selected={selectedIndex === index}
                      button
                      key={conversation.id}
                      onClick={() => {
                        onConversationSelect(conversation, index);
                      }}
                      sx={{
                        backgroundColor: selectedIndex === index ? '#E3F2FD' : 'transparent',
                        borderLeft: selectedIndex === index ? '4px solid #1976D2' : '4px solid transparent',
                        '&:hover': {
                          backgroundColor: selectedIndex === index ? '#BBDEFB' : '#F5F5F5',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#E3F2FD !important',
                          borderLeft: '4px solid #1976D2',
                          '&:hover': {
                            backgroundColor: '#BBDEFB !important',
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Avatar 
                          sx={{ 
                            backgroundColor: getConversationColor(conversation.id),
                            color: '#fff',
                            fontWeight: 'bold'
                          }}
                        >
                          {conversation.author?.email?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" noWrap sx={{ flex: 1, color: '#333333' }}>
                            {conversation.author?.email || 'Utilisateur inconnu'}
                        </Typography>
                        
                        {/* Icône de bordereau si disponible */}
                        {conversation.author?.id && annonce?.id && checkoutsByConversation[conversation.id] && (
                          <Tooltip title="Voir les détails du bordereau">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCheckoutDetails(conversation);
                              }}
                              sx={{
                                color: '#1b9098',
                                '&:hover': {
                                  backgroundColor: '#E3F2FD',
                                },
                              }}
                            >
                              <ReceiptIcon sx={{ fontSize: '18px' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {conversation.messages?.length > 0 
                          ? conversation.messages[conversation.messages.length - 1]?.content?.substring(0, 30) + '...'
                          : 'Aucun message'
                        }
                          </Typography>
                        }
                      />
                    </UserItem>
              );
            })}
              </List>
            </UserList>
    );
  };

  // 1. Ajout d'une variable pour la marge basse dynamique
  const bottomSpacing = isMobile ? 140 : 0; // 140px pour laisser la place aux barres fixes

  return (
    <Box sx={{ height: '100%', p: isMobile ? 0 : 2 }}>
      <ToastContainer />
      <ChatContainer isMobile={isMobile}>
        <Grid container sx={{ height: '100%' }}>
          {/* Liste des conversations - visible sur desktop, cachée sur mobile */}
          {!isMobile && (
            <Grid item xs={12} md={4} sx={{ height: '100%' }}>
              <ConversationList
                conversations={conversations}
                selectedIndex={selectedIndex}
                onConversationSelect={handleConversation}
                onDrawerClose={handleDrawerClose}
                isMobile={isMobile}
                onRefresh={forceRefreshConversations}
              />
          </Grid>
          )}

          {/* Zone des messages */}
          <Grid item xs={12} md={isMobile ? 12 : 8} sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%',
            pb: 0,
          }}>
            {/* Header avec titre de la conversation sur mobile */}
            {isMobile && currentConversation && currentConversation.id && (
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid #DFE3E8',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#212B36' }}>
                  {currentConversation.author?.email || 'Utilisateur inconnu'}
                </Typography>
                <IconButton onClick={toggleDrawer} size="small" sx={{ color: '#637381' }}>
                  <MenuIcon />
                </IconButton>
              </Box>
            )}

            {/* Message d'état si aucune conversation sélectionnée sur mobile */}
            {isMobile && (!currentConversation || !currentConversation.id) && conversations.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                p: 3,
                textAlign: 'center',
                backgroundColor: '#F9FAFB'
              }}>
                <Iconify icon="eva:message-circle-outline" width={64} height={64} sx={{ mb: 2, opacity: 0.5, color: '#637381' }} />
                <Typography variant="h6" sx={{ mb: 1, color: '#637381', fontWeight: 600 }}>
                  Sélectionnez une conversation
                </Typography>
                <Typography variant="body2" sx={{ color: '#637381', mb: 2 }}>
                  Choisissez une conversation dans la liste pour commencer à discuter
                </Typography>
                <Button
                  variant="contained"
                  onClick={toggleDrawer}
                  startIcon={<MenuIcon />}
                  sx={{
                    backgroundColor: '#1b9098',
                    '&:hover': {
                      backgroundColor: '#135F64',
                    },
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Voir les conversations
                </Button>
              </Box>
            )}

            {/* Message si aucune conversation */}
            {conversations.length === 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                p: 3,
                textAlign: 'center',
                backgroundColor: '#F9FAFB'
              }}>
                <Iconify icon="eva:message-circle-outline" width={64} height={64} sx={{ mb: 2, opacity: 0.5, color: '#637381' }} />
                <Typography variant="h6" sx={{ mb: 1, color: '#637381', fontWeight: 600 }}>
                  Aucune conversation
                </Typography>
                <Typography variant="body2" sx={{ color: '#637381' }}>
                  Aucune conversation n'est disponible pour le moment
                </Typography>
              </Box>
            )}

            <MessageList ref={listRef} isMobile={isMobile} sx={isMobile ? { pb: '140px !important', mb: '71px' } : { pb: 0 }}>
              {currentMessages.length > 0 &&
                groupMessagesByDate(currentMessages
                  .filter(m => m.content && m.content.trim() !== '')
                ).map((group, groupIndex) => (
                  <Box key={group.date}>
                    {group.displayDate && (
                      <Box sx={{ 
                        textAlign: 'center', 
                        padding: '10px 0', 
                        backgroundColor: '#F0F2F5', 
                        borderRadius: '10px', 
                        margin: '10px 0', 
                        fontSize: '0.875rem', 
                        color: '#637381', 
                        fontWeight: 600 
                      }}>
                        {group.displayDate}
                      </Box>
                    )}
                    {group.messages
                      .filter(message => message.content && message.content.trim() !== '')
                      .map((message) => {
                        const isLastAuthorMessage = !message.author && message.id === Math.max(...group.messages.filter(m => !m.author).map(m => m.id));
                        return (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: message.author ? 'flex-start' : 'flex-end',
                    }}
                  >
                    {message.isPicture ? (
                      <Box
                        sx={{
                          maxWidth: '70%',
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.9,
                          },
                        }}
                        onClick={() => handleOpenDialogue(message.content)}
                      >
                        <Image
                          className="picture-chat"
                          src={getPicture(message.content)}
                          style={{ borderRadius: '12px' }}
                        />
                      </Box>
                    ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: !message.author ? 'flex-end' : 'flex-start' }}>
                                <MessageBubble 
                                  isAuthor={!message.author} 
                                  isRead={message.isRead}
                                  isLastAuthorMessage={isLastAuthorMessage}
                                  conversationColor={getConversationColor(currentConversation.id)}
                                  isNew={newMessageIds.has(message.id || message.createdAt)}
                                  onContextMenu={(e) => handleMessageContextMenu(e, message)}
                                  sx={{
                                    cursor: isDeliveryMessage(message) ? 'default' : 'pointer',
                                    userSelect: 'none',
                                    '&:hover': {
                                      transform: isDeliveryMessage(message) ? 'none' : 'scale(1.02)',
                                      transition: 'transform 0.2s ease',
                                    },
                                    '&:active': {
                                      transform: isDeliveryMessage(message) ? 'none' : 'scale(0.98)',
                                    },
                                  }}
                                >
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            width: '100%',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            maxWidth: '100%',
                            lineHeight: 1.5
                          }}
                        >
                          {message.content}
                        </Typography>
                      </MessageBubble>
                                <MessageTime isAuthor={!message.author}>{formatTime(message.createdAt)}</MessageTime>
                              </Box>
                    )}
                          </Box>
                        );
                      })}
                  </Box>
                ))}
            </MessageList>

            {/* Barre d'actions mobile collée à la zone de saisie */}
            {isMobile && (
              <Box sx={{
                width: '100%',
                backgroundColor: '#ffffff',
                borderTop: '1px solid #DFE3E8',
                padding: '4px 8px',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                zIndex: 999,
                gap: 1,
                height: '50px',
                position: 'static',
                mb: 0.5,
              }}>
                {/* Restitué en main propre */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <IconButton onClick={() => handleClickOpen('restitution')} sx={{ color: '#54D62C', width: 32, height: 32, '&:hover': { backgroundColor: '#E9FCD4' }, mb: 0 }}>
                    <HandshakeIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#54D62C', fontWeight: 600, textAlign: 'center', lineHeight: 1, height: 'auto', display: 'flex', alignItems: 'center', mt: 0.25 }}>
                    Remis en main propre
                  </Typography>
                </Box>
                {/* Je valide */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <IconButton onClick={handleMessageOk} disabled={!currentConversation || !currentConversation.id} sx={{ color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#54D62C', width: 32, height: 32, '&:hover': { backgroundColor: !currentConversation || !currentConversation.id ? 'transparent' : '#E9FCD4' }, '&:disabled': { color: '#C4CDD5' }, mb: 0 }}>
                    <Iconify icon="eva:checkmark-circle-2-fill" sx={{ fontSize: '18px' }} />
                  </IconButton>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#54D62C', fontWeight: 600, textAlign: 'center', lineHeight: 1, height: 'auto', display: 'flex', alignItems: 'center', mt: 0.25 }}>
                    Je valide
                  </Typography>
                </Box>
                {/* Je ne valide pas */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <IconButton onClick={handleMessageNotOk} disabled={!currentConversation || !currentConversation.id} sx={{ color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#FF4842', width: 32, height: 32, '&:hover': { backgroundColor: !currentConversation || !currentConversation.id ? 'transparent' : '#FFF7CD' }, '&:disabled': { color: '#C4CDD5' }, mb: 0 }}>
                    <CancelIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#FF4842', fontWeight: 600, textAlign: 'center', lineHeight: 1, height: 'auto', display: 'flex', alignItems: 'center', mt: 0.25 }}>
                    Je ne valide pas
                  </Typography>
                </Box>
                {/* Envoyer bon de livraison */}
                {objectStatus === "TROUVÉ" && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <IconButton onClick={() => handleClickOpen('lien')} disabled={!currentConversation || !currentConversation.id} sx={{ color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#3366FF', width: 32, height: 32, '&:hover': { backgroundColor: !currentConversation || !currentConversation.id ? 'transparent' : '#D6E4FF' }, '&:disabled': { color: '#C4CDD5' }, mb: 0 }}>
                      <LocalShippingIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#FFC107', fontWeight: 600, textAlign: 'center', lineHeight: 1, height: 'auto', display: 'flex', alignItems: 'center', mt: 0.25 }}>
                      Annuler restitution
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <InputContainer isMobile={isMobile}>
              {imageUpload === null ? (
                <ImageUploading
                  value={images}
                  onChange={handlePicture}
                  maxNumber={1}
                  dataURLKey="data_url"
                >
                  {({ onImageUpload, dragProps }) => (
                  <IconButton
                      onClick={onImageUpload}
                      {...dragProps}
                    disabled={!currentConversation || !currentConversation.id}
                    sx={{
                      color: '#1b9098',
                      '&:hover': {
                        backgroundColor: '#F4F6F8',
                      },
                      '&:disabled': {
                        color: '#C4CDD5',
                      },
                    }}
                    >
                    <CameraAltIcon />
                  </IconButton>
                  )}
                </ImageUploading>
              ) : (
                <IconButton 
                  size="small" 
                  onClick={deletePicture}
                  sx={{
                    color: '#FF4842',
                    '&:hover': {
                      backgroundColor: '#FFF7CD',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}

              <Box sx={{ position: 'relative', flex: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                  placeholder={currentConversation && currentConversation.id ? "Écrivez votre message..." : "Sélectionnez une conversation pour commencer..."}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                  disabled={!currentConversation || !currentConversation.id}
                sx={{
                  '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      fontSize: isMobile ? '16px' : '14px',
                      paddingRight: message.trim() ? '50px' : '16px',
                      minHeight: '40px',
                      maxHeight: '120px',
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #DFE3E8',
                      '& .MuiOutlinedInput-input': {
                        padding: '10px 16px',
                        color: '#212B36',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        border: '1px solid #1b9098',
                      },
                  },
                }}
              />

                {message.trim() && !loadingSendMessage && (
                  <IconButton
                  onClick={handleMessage}
                    disabled={!currentConversation || !currentConversation.id}
                    sx={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: '#1b9098',
                      color: 'white',
                      width: '32px',
                      height: '32px',
                      '&:hover': {
                        backgroundColor: '#135F64',
                        transform: 'translateY(-50%) scale(1.1)',
                      },
                      '&:disabled': {
                        backgroundColor: '#C4CDD5',
                        color: 'white',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                )}
                
                {loadingSendMessage && (
                  <Box sx={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}>
                    <CustomSpinner />
                  </Box>
              )}
              </Box>
            </InputContainer>

            {/* Barre d'actions mobile */}
            {isMobile && (
              <Box sx={{
                position: 'fixed',
                bottom: '71px',
                left: 0,
                right: 0,
                backgroundColor: '#ffffff',
                borderTop: '1px solid #DFE3E8',
                padding: '4px 8px',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                zIndex: 999,
                gap: 1,
                height: '50px',
              }}>
                
                {/* Restitué en main propre */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}>
                  <IconButton
                    onClick={() => handleClickOpen('restitution')}
                    sx={{
                      color: '#54D62C',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        backgroundColor: '#E9FCD4',
                      },
                      mb: 0,
                    }}
                  >
                    <HandshakeIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                  <Typography 
                    variant="caption" 
                    sx={{
                      fontSize: '0.6rem',
                      color: '#54D62C',
                      fontWeight: 600,
                      textAlign: 'center',
                      lineHeight: 1,
                      height: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      mt: 0.25,
                    }}
                  >
                    Remis en main propre
                  </Typography>
                </Box>

                {/* Je valide */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}>
                  <IconButton
            onClick={handleMessageOk}
                    disabled={!currentConversation || !currentConversation.id}
            sx={{
                      color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#54D62C',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        backgroundColor: !currentConversation || !currentConversation.id ? 'transparent' : '#E9FCD4',
                      },
                      '&:disabled': {
                        color: '#C4CDD5',
                      },
                      mb: 0,
                    }}
                  >
                    <Iconify icon="eva:checkmark-circle-2-fill" sx={{ fontSize: '18px' }} />
                  </IconButton>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.6rem',
                      color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#54D62C',
                      fontWeight: 600,
                      textAlign: 'center',
                      lineHeight: 1,
                      height: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      mt: 0.25,
                    }}
                  >
                    Je valide
                  </Typography>
                </Box>

                {/* Je ne valide pas */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}>
                  <IconButton
            onClick={handleMessageNotOk}
                    disabled={!currentConversation || !currentConversation.id}
            sx={{
                      color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#FF4842',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        backgroundColor: !currentConversation || !currentConversation.id ? 'transparent' : '#FFF7CD',
                      },
                      '&:disabled': {
                        color: '#C4CDD5',
                      },
                      mb: 0,
                    }}
                  >
                    <CancelIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                  <Typography 
                    variant="caption" 
                    sx={{
                      fontSize: '0.6rem',
                      color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#FF4842',
                      fontWeight: 600,
                      textAlign: 'center',
                      lineHeight: 1,
                      height: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      mt: 0.25,
                    }}
                  >
                    Je ne valide pas
                  </Typography>
                </Box>

                {/* Envoyer bon de livraison */}
                {objectStatus === "TROUVÉ" && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                  }}>
                    <IconButton
                    onClick={() => handleClickOpen('lien')}
                      disabled={!currentConversation || !currentConversation.id}
                      sx={{
                        color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#3366FF',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          backgroundColor: !currentConversation || !currentConversation.id ? 'transparent' : '#D6E4FF',
                        },
                        '&:disabled': {
                          color: '#C4CDD5',
                        },
                        mb: 0,
                      }}
                    >
                      <LocalShippingIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                    <Typography 
                      variant="caption" 
                sx={{
                        fontSize: '0.6rem',
                        color: !currentConversation || !currentConversation.id ? '#C4CDD5' : '#3366FF',
                        fontWeight: 600,
                        textAlign: 'center',
                        lineHeight: 1,
                        height: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        mt: 0.25,
                      }}
                    >
                      Bon de livraison
                    </Typography>
                  </Box>
                )}

                {/* Annuler la restitution */}
                {annonce.status === 'RESTITUÉ' && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                  }}>
                    <IconButton
                      onClick={() => handleClickOpen('annulRestitution')}
                      sx={{
                        color: '#FFC107',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          backgroundColor: '#FFF3C4',
                        },
                        mb: 0,
                      }}
                    >
                      <CancelIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                    <Typography 
                      variant="caption" 
                      sx={{
                        fontSize: '0.6rem',
                        color: '#FFC107',
                        fontWeight: 600,
                        textAlign: 'center',
                        lineHeight: 1,
                        height: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        mt: 0.25,
                      }}
                    >
                      Annuler restitution
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
        </Grid>
      </Grid>
      </ChatContainer>

      {/* Drawer pour mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
              sx={{
          zIndex: 9999,
          '& .MuiDrawer-paper': {
            width: '280px',
            boxSizing: 'border-box',
            border: 'none',
            borderRight: '1px solid #DFE3E8',
            backgroundColor: '#ffffff',
            zIndex: 9999,
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9998,
          },
        }}
        ModalProps={{
          keepMounted: true, // Améliore les performances sur mobile
        }}
        variant="temporary"
      >
        <ConversationList
          conversations={conversations}
          selectedIndex={selectedIndex}
          onConversationSelect={handleConversation}
          onDrawerClose={handleDrawerClose}
          isMobile={true}
          onRefresh={forceRefreshConversations}
        />
      </Drawer>

      <Box sx={{ mt: 2, display: isMobile ? 'none' : 'flex', gap: 2, justifyContent: 'center', flexWrap: 'nowrap' }}>
        {/* Restitué en main propre */}
        {annonce.status !== 'RESTITUÉ' && (
          <Button
              variant="contained"
            onClick={() => handleClickOpen('restitution')}
                sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: '140px',
              height: '48px',
              backgroundColor: '#8B5CF6',
              color: 'white',
              borderRadius: '8px',
              border: '1px solid #8B5CF6',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#6D28D9',
                borderColor: '#6D28D9',
              },
            }}
          >
            <HandshakeIcon sx={{ fontSize: '20px' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Restitué en main propre
            </Typography>
          </Button>
        )}
        
        {/* Je valide */}
        <Button
            variant="contained"
          onClick={handleMessageOk}
            sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: '120px',
            height: '48px',
            backgroundColor: '#54D62C',
            color: 'white',
            borderRadius: '8px',
            border: '1px solid #54D62C',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#229A16',
              borderColor: '#229A16',
            },
          }}
          >
          <Iconify icon="eva:checkmark-circle-2-fill" sx={{ fontSize: '20px' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            Je valide
          </Typography>
        </Button>
        
        {/* Je ne valide pas */}
        <Button
              variant="contained"
          onClick={handleMessageNotOk}
              sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: '140px',
            height: '48px',
            backgroundColor: '#FF4842',
            color: 'white',
            borderRadius: '8px',
            border: '1px solid #FF4842',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#B72136',
              borderColor: '#B72136',
            },
          }}
        >
          <CancelIcon sx={{ fontSize: '20px' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            Je ne valide pas
          </Typography>
        </Button>
        
        {/* Envoyer bon de livraison */}
        {objectStatus === "TROUVÉ" && (
          <Button
              variant="contained"
            onClick={() => handleClickOpen('lien')}
              sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: '160px',
              height: '48px',
              backgroundColor: '#3366FF',
              color: 'white',
              borderRadius: '8px',
              border: '1px solid #3366FF',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#1939B7',
                borderColor: '#1939B7',
              },
            }}
          >
            <LocalShippingIcon sx={{ fontSize: '20px' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Envoyer bon de livraison
            </Typography>
          </Button>
          )}
        
        {/* Imprimer bon de livraison - A_EXPEDIER */}
        {objectStatus === "A_EXPEDIER" && (
          <Button
            variant="contained"
            onClick={() => handleImprimerLeBon(objectStatus)}
            disabled={isLoading}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: '160px',
              height: '48px',
              backgroundColor: isLoading ? '#C4CDD5' : '#637381',
              color: 'white',
              borderRadius: '8px',
              border: '1px solid #637381',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isLoading ? '#C4CDD5' : '#454F5B',
                borderColor: '#454F5B',
              },
              '&:disabled': {
                backgroundColor: '#C4CDD5',
                color: '#637381',
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <LocalShippingIcon sx={{ fontSize: '20px' }} />
            )}
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {isLoading ? 'Téléchargement...' : 'Imprimer bon de livraison'}
            </Typography>
          </Button>
        )}
        
        {/* Imprimer bon de livraison - ENVOYÉ */}
        {objectStatus === "ENVOYÉ" && (
          <Button
            variant="contained"
            onClick={() => handleImprimerLeBon(objectStatus)}
            disabled={isLoading}
              sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: '160px',
              height: '48px',
              backgroundColor: isLoading ? '#C4CDD5' : '#637381',
              color: 'white',
              borderRadius: '8px',
              border: '1px solid #637381',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isLoading ? '#C4CDD5' : '#454F5B',
                borderColor: '#454F5B',
              },
              '&:disabled': {
                backgroundColor: '#C4CDD5',
                color: '#637381',
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <LocalShippingIcon sx={{ fontSize: '20px' }} />
            )}
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '0.875rem' }}>
              {isLoading ? 'Téléchargement...' : 'Imprimer bon de livraison'}
            </Typography>
          </Button>
        )}
        
        {/* Annuler la restitution */}
        {annonce.status === 'RESTITUÉ' && (
          <Button
              variant="contained"
              onClick={() => handleClickOpen('annulRestitution')}
              sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: '160px',
              height: '48px',
              backgroundColor: '#FFC107',
              color: '#212B36',
              borderRadius: '8px',
              border: '1px solid #FFC107',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#B78103',
                borderColor: '#B78103',
                color: 'white',
              },
            }}
          >
            <CancelIcon sx={{ fontSize: '20px' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Annuler la restitution
            </Typography>
          </Button>
          )}
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{actionSentence}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{actionMessage}</DialogContentText>
          <br />
          {dialogRestitution &&
            <>
              <TextField
                label="Détail de la restitution (facultatif)"
                fullWidth
            />
            </>
          }
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleClose} autoFocus>
            ne rien faire
          </Button>
          <Button variant="contained" onClick={handleAction}>
            {action}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal pour les détails du bordereau */}
      <Dialog
        open={showCheckoutModal}
        onClose={() => {
          setShowCheckoutModal(false);
          setCurrentCheckoutConversationId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid #DFE3E8',
          pb: 2
        }}>
          <ReceiptIcon sx={{ color: '#1b9098' }} />
          <Typography variant="h6">Détails du bordereau de livraison</Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {checkoutLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : checkoutDetails ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Statut du bordereau
                </Typography>
                <Chip 
                  label={(() => {
                    switch(checkoutDetails.status) {
                      case 'WAITING': return 'EN ATTENTE';
                      case 'WAITING_PAYMENT': return 'EN ATTENTE DE PAIEMENT';
                      case 'PAID': return 'PAYÉ';
                      default: return checkoutDetails.status || 'EN ATTENTE';
                    }
                  })()} 
                  color={(() => {
                    switch(checkoutDetails.status) {
                      case 'PAID': return 'success';
                      case 'WAITING_PAYMENT': return 'warning';
                      default: return 'default';
                    }
                  })()}
                  size="small"
                />
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  ID du bordereau
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', backgroundColor: '#F5F5F5', p: 1, borderRadius: 1 }}>
                  {checkoutDetails.id}
                </Typography>
              </Box>
              
              {checkoutDetails.createdAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Date de création
                  </Typography>
                  <Typography variant="body1">
                    {new Date(checkoutDetails.createdAt).toLocaleString('fr-FR')}
                  </Typography>
                </Box>
              )}
              
              {checkoutDetails.updatedAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Dernière mise à jour
                  </Typography>
                  <Typography variant="body1">
                    {new Date(checkoutDetails.updatedAt).toLocaleString('fr-FR')}
                  </Typography>
                </Box>
              )}
              
              {/* Email de l'utilisateur */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Email de l'utilisateur
                </Typography>
                <Typography variant="body1" sx={{ 
                  backgroundColor: '#F5F5F5', 
                  p: 1.5, 
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.9rem'
                }}>
                  {currentConversation?.author?.email || 'Email non disponible'}
                </Typography>
              </Box>

              {/* Vignette de l'annonce */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Annonce concernée
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  backgroundColor: '#F8F9FA',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #E9ECEF'
                }}>
                  {(() => {
                    // Construire l'URL de l'image à partir du filePath
                    const imageUrl = annonce?.filePath ? 
                      `${process.env.REACT_APP_PUBLIC_URL_ANNONCES_IMAGES}/${annonce.filePath}` : 
                      null;
                    
                    return imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt="Vignette de l'annonce"
                        style={{
                          width: '60px',
                          height: '60px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #E9ECEF'
                        }}
                        onError={(e) => {
                          // En cas d'erreur de chargement, masquer l'image
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#E9ECEF',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid #E9ECEF'
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          Pas d'image
                        </Typography>
                      </Box>
                    );
                  })()}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {annonce?.origin || 'Annonce'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {annonce?.id || 'Non disponible'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {annonce?.placeDescription || annonce?.ville || 'Lieu non disponible'}
                    </Typography>
                    {annonce?.date && (
                      <Typography variant="body2" color="text.secondary">
                        {new Date(annonce.date).toLocaleDateString('fr-FR')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Informations personnelles */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Informations personnelles
                </Typography>
                
                {(() => {
                  const hasPersonalInfo = checkoutDetails.firstName || checkoutDetails.lastName || 
                                        checkoutDetails.email || checkoutDetails.phone || 
                                        checkoutDetails.address || checkoutDetails.city || 
                                        checkoutDetails.zipCode || checkoutDetails.country;
                  
                  if (!hasPersonalInfo) {
                    return (
                      <Box sx={{ 
                        backgroundColor: '#FFF3CD', 
                        border: '1px solid #FFEAA7', 
                        borderRadius: 1, 
                        p: 2 
                      }}>
                        <Typography variant="body2" color="#856404">
                          ⚠️ La personne n'a pas encore rempli son bordereau de livraison.
                        </Typography>
                      </Box>
                    );
                  }
                  
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {checkoutDetails.firstName && checkoutDetails.lastName && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Nom complet
                          </Typography>
                          <Typography variant="body1">
                            {checkoutDetails.firstName} {checkoutDetails.lastName}
                          </Typography>
                        </Box>
                      )}
                      
                      {checkoutDetails.email && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Email
                          </Typography>
                          <Typography variant="body1">
                            {checkoutDetails.email}
                          </Typography>
                        </Box>
                      )}
                      
                      {checkoutDetails.phone && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Téléphone
                          </Typography>
                          <Typography variant="body1">
                            {checkoutDetails.phone}
                          </Typography>
                        </Box>
                      )}
                      
                      {(checkoutDetails.address || checkoutDetails.addressComplement || checkoutDetails.city || checkoutDetails.zipCode || checkoutDetails.country) && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Adresse
                          </Typography>
                          <Typography variant="body1">
                            {checkoutDetails.address && `${checkoutDetails.address}`}
                            {checkoutDetails.addressComplement && `\n${checkoutDetails.addressComplement}`}
                            {checkoutDetails.zipCode && checkoutDetails.city && `\n${checkoutDetails.zipCode} ${checkoutDetails.city}`}
                            {checkoutDetails.country && `\n${checkoutDetails.country}`}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Box>
              
              {checkoutDetails.userId && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ID Utilisateur
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', backgroundColor: '#F5F5F5', p: 1, borderRadius: 1 }}>
                    {checkoutDetails.userId}
                  </Typography>
                </Box>
              )}
              
              {checkoutDetails.annonceId && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ID Annonce
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', backgroundColor: '#F5F5F5', p: 1, borderRadius: 1 }}>
                    {checkoutDetails.annonceId}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary">
              Aucun bordereau trouvé pour cette conversation.
            </Typography>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={() => setShowCheckoutModal(false)}
            variant="outlined"
          >
            Fermer
          </Button>
          
          {checkoutDetails && checkoutDetails.status !== 'PAID' && (
            <Button 
              onClick={handleDeleteCheckoutClick}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
            >
              Supprimer le bordereau
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Boîte de dialogue de confirmation pour supprimer le bordereau */}
      <Dialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir supprimer ce bordereau de livraison ?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cette action est irréversible et supprimera définitivement le bordereau.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={() => setShowDeleteConfirmation(false)}
            variant="outlined"
          >
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteCheckout}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menu contextuel pour supprimer les messages */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem 
          onClick={handleDeleteMessage}
          sx={{
            color: '#FF4842',
            '&:hover': {
              backgroundColor: '#FFF5F5',
            },
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: '18px' }} />
          Supprimer le message
        </MenuItem>
      </Menu>
    </Box>
  );
}
